// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Settings Validation
 *
 * Validates lobby settings against schema constraints and business rules.
 */

import type { GameConfigSchema, LobbySettings } from '../../types/game';

/**
 * Validate a single setting value against schema
 *
 * @param settingName - The setting key (e.g., "max_players")
 * @param value - The value to validate
 * @param schema - The full config schema
 * @param currentPlayerCount - Optional current player count for max_players validation
 * @returns Error message if invalid, null if valid
 */
export function validateSetting(
  settingName: string,
  value: number | boolean | string,
  schema: GameConfigSchema,
  currentPlayerCount?: number
): string | null {
  const config = schema[settingName];

  if (!config) {
    return `Unknown setting: ${settingName}`;
  }

  // Type validation
  if (config.type === 'number') {
    if (typeof value !== 'number' || isNaN(value)) {
      return `${settingName} must be a number`;
    }

    // Range validation
    if (config.min !== undefined && value < config.min) {
      return `${settingName} must be at least ${config.min}`;
    }
    if (config.max !== undefined && value > config.max) {
      return `${settingName} must be at most ${config.max}`;
    }
  } else if (config.type === 'boolean') {
    if (typeof value !== 'boolean') {
      return `${settingName} must be a boolean`;
    }
  } else if (config.type === 'string') {
    if (typeof value !== 'string') {
      return `${settingName} must be a string`;
    }
  }

  // Business rule: max_players cannot be less than current player count
  if (
    settingName === 'max_players' &&
    currentPlayerCount !== undefined &&
    typeof value === 'number'
  ) {
    if (value < currentPlayerCount) {
      return `Cannot set max players (${value}) below current player count (${currentPlayerCount})`;
    }
  }

  // Business rule: min_players cannot be greater than max_players
  if (settingName === 'min_players') {
    // This will be validated in batch validation
  }

  return null;
}

/**
 * Validate multiple settings at once
 *
 * @param settings - Partial settings to validate
 * @param schema - The full config schema
 * @param currentPlayerCount - Optional current player count
 * @returns Object mapping setting names to error messages (empty if all valid)
 */
export function validateSettingsBatch(
  settings: Partial<LobbySettings>,
  schema: GameConfigSchema,
  currentPlayerCount?: number
): Record<string, string> {
  const errors: Record<string, string> = {};

  // Validate each setting individually
  Object.entries(settings).forEach(([key, value]) => {
    if (value === undefined || value === null) return; // Skip undefined/null values
    if (Array.isArray(value)) return; // Skip array values (e.g., enabled_packs)
    const error = validateSetting(key, value, schema, currentPlayerCount);
    if (error) {
      errors[key] = error;
    }
  });

  // Cross-field validation
  const min = settings.min_players;
  const max = settings.max_players;

  if (min !== undefined && max !== undefined && min > max) {
    errors.min_players = `Minimum players (${min}) cannot exceed maximum players (${max})`;
  }

  return errors;
}

/**
 * Check if all settings are valid
 *
 * @param settings - Settings to validate
 * @param schema - The full config schema
 * @param currentPlayerCount - Optional current player count
 * @returns True if all valid, false otherwise
 */
export function areSettingsValid(
  settings: Partial<LobbySettings>,
  schema: GameConfigSchema,
  currentPlayerCount?: number
): boolean {
  const errors = validateSettingsBatch(settings, schema, currentPlayerCount);
  return Object.keys(errors).length === 0;
}

/**
 * Format a setting name for display
 *
 * @param key - Setting key (e.g., "max_players")
 * @returns Formatted name (e.g., "Max Players")
 */
export function formatSettingName(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
